import type { TFunction } from "i18next";

const normalizeText = (value: string) => value.trim().replace(/\s+/g, " ");

export const truncateText = (text: string, maxLength: number) => {
  const normalizedText = normalizeText(text);
  if (normalizedText.length <= maxLength) return normalizedText;

  const slicedText = normalizedText.slice(0, maxLength);
  const lastSpaceIndex = slicedText.lastIndexOf(" ");

  const shouldCutBySpace = lastSpaceIndex > Math.floor(maxLength * 0.6);
  const cutIndex = shouldCutBySpace ? lastSpaceIndex : maxLength;

  return `${normalizedText.slice(0, cutIndex).trimEnd()}...`;
};

const formatNumber = (value: number) => {
  if (!Number.isFinite(value)) return "";
  if (Number.isInteger(value)) return value.toString();
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const getValidNumbers = (values: Array<number | null | undefined>) =>
  values.filter(
    (value): value is number =>
      typeof value === "number" && Number.isFinite(value)
  );

export const buildAssetUrl = (apiUrl: string, path?: string | null) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${apiUrl}${path.startsWith("/") ? "" : "/"}${path}`;
};

export const formatPriceText = (
  portions: Array<{ price: number | null | undefined }>,
  currency: string,
  t: TFunction
) => {
  const prices = getValidNumbers(portions.map((portion) => portion.price));
  if (prices.length === 0) return `0 ${currency}`;

  const minimumPrice = Math.min(...prices);
  const isFrom = prices.length > 1;

  return `${isFrom ? t("from ") : ""}${formatNumber(minimumPrice)} ${currency}`;
};

export const formatVolumeText = (
  portions: Array<{ volume: number | null | undefined }>,
  unitName?: string | null
) => {
  const normalizedUnitName = unitName ? normalizeText(unitName) : "";
  if (!normalizedUnitName) return null;

  const volumes = getValidNumbers(portions.map((portion) => portion.volume));
  if (volumes.length === 0) return null;

  const uniqueSortedVolumes = Array.from(new Set(volumes)).sort(
    (a, b) => a - b
  );
  return `${uniqueSortedVolumes.map(formatNumber).join("/")} ${normalizedUnitName}`;
};

export const formatMinVolumeText = (
  portions: Array<{ volume: number | null | undefined }>,
  unitName: string,
  t: TFunction
): string => {
  const normalizedUnitName = unitName ? normalizeText(unitName) : "";
  if (!normalizedUnitName) return "";

  const volumeValues = getValidNumbers(
    portions.map((portion) => portion.volume)
  );
  if (volumeValues.length === 0) return "";

  const uniqueSortedVolumes = Array.from(new Set(volumeValues)).sort(
    (a, b) => a - b
  );
  const minVolume = uniqueSortedVolumes[0];

  const shouldShowFrom = uniqueSortedVolumes.length > 1;

  return `${shouldShowFrom ? t("from ") : ""}${formatNumber(minVolume)} ${normalizedUnitName}`;
};
